/**
 * @fileoverview Analysis Panel Dialog Component
 * @description Full-screen dialog showing full semantic + agentic analysis
 *              results for a selected incident. Opens as a popup overlay.
 */

import React from 'react';
import {
  Typography,
  Box,
  Chip,
  Grid,
  Divider,
  IconButton,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  Alert,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';
import {
  X,
  Brain,
  Bot,
  CheckCircle,
  TrendingUp,
  Users,
  FileText,
  ArrowUp,
  ArrowDown,
  AlertTriangle,
  ChevronDown,
} from 'lucide-react';

const severityColor = (severity) => {
  if (!severity) return 'default';
  const s = severity.toLowerCase();
  if (s === 'critical' || s === 'p1') return 'error';
  if (s === 'high' || s === 'p2') return 'warning';
  if (s === 'medium' || s === 'p3') return 'info';
  if (s === 'low' || s === 'p4') return 'success';
  if (s === 'informational' || s === 'p5') return 'default';
  return 'default';
};

const AnalysisPanel = ({ result, incident: incidentProp, open, onClose, inline = false }) => {
  if (!result) return null;

  const {
    incident_id,
    description,
    short_description,
    close_description,
    assigned_team,
    original_severity,
    category,
    status,
    semantic_severity,
    semantic_continuous_priority,
    semantic_probable_team,
    semantic_reason,
    agentic_severity,
    agentic_continuous_priority,
    agentic_probable_team,
    agentic_reason,
    severity_match,
    team_match,
    mismatch_type,
    overall_priority,
    overall_recommendation,
    similar_incidents = [],
  } = result;

  // Merge incident record fields (from the fetch) with analysis result fields
  const _inc = incidentProp || {};

  // ── Shared content (used in both inline & dialog modes) ──
  const content = (
    <>

      {/* ── Severity Mismatch Banner ─────────────────────────── */}
      {mismatch_type === 'under_triaged' && (
        <Alert
          severity="error"
          icon={<AlertTriangle size={20} />}
          sx={{ mb: 2, fontWeight: 600 }}
        >
          <strong>Potential Under-Triage Detected</strong> — Human classified this incident as{' '}
          <strong>{original_severity}</strong>, but AI assessed it as{' '}
          <strong>{agentic_severity}</strong>. This may be more critical than recorded and could
          require immediate re-evaluation.
        </Alert>
      )}

      {mismatch_type === 'over_triaged' && (
        <Accordion
          disableGutters
          elevation={0}
          sx={{
            mb: 2,
            border: '1px solid',
            borderColor: 'warning.light',
            borderRadius: '8px !important',
            '&:before': { display: 'none' },
          }}
        >
          <AccordionSummary expandIcon={<ChevronDown size={16} />} sx={{ minHeight: 40, px: 2 }}>
            <Stack direction="row" alignItems="center" spacing={1}>
              <AlertTriangle size={16} color="#ed6c02" />
              <Typography variant="body2" fontWeight={600} color="warning.dark">
                AI Suggests Lower Severity — Human: <strong>{original_severity}</strong>, AI:{' '}
                <strong>{agentic_severity}</strong> (click to expand)
              </Typography>
            </Stack>
          </AccordionSummary>
          <AccordionDetails sx={{ pt: 0, px: 2, pb: 1.5 }}>
            <Typography variant="body2" color="text.secondary">
              The human-assigned severity ({original_severity}) is higher than the AI assessment (
              {agentic_severity}). This may indicate over-triage. Review the AI reasoning below to
              decide if re-classification is appropriate.
            </Typography>
          </AccordionDetails>
        </Accordion>
      )}

      <Grid container spacing={2}>
        {/* Semantic Analysis */}
        <Grid item xs={12} md={4}>
          <Box className="analysis-section analysis-section-semantic">
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.5 }}>
              <Brain size={20} />
              <Typography variant="subtitle1" fontWeight={700}>
                Semantic Analysis
              </Typography>
            </Stack>

            <Stack spacing={1.5}>
              <Box>
                <Typography variant="caption" color="text.secondary">Severity</Typography>
                <Chip
                  label={semantic_severity}
                  size="small"
                  color={severityColor(semantic_severity)}
                  sx={{ ml: 1 }}
                />
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Continuous Priority
                </Typography>
                <Typography variant="body2" fontWeight={700}>
                  {semantic_continuous_priority?.toFixed(2)} / 10
                </Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">Probable Team</Typography>
                <Typography variant="body2" fontWeight={600}>
                  {semantic_probable_team}
                </Typography>
              </Box>
              <Divider />
              <Box>
                <Typography variant="caption" color="text.secondary">Reason</Typography>
                <Typography variant="body2" sx={{ whiteSpace: 'pre-line', fontSize: '0.78rem' }}>
                  {semantic_reason}
                </Typography>
              </Box>
            </Stack>
          </Box>
        </Grid>

        {/* Agentic Analysis */}
        <Grid item xs={12} md={4}>
          <Box className="analysis-section analysis-section-agentic">
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.5 }}>
              <Bot size={20} />
              <Typography variant="subtitle1" fontWeight={700}>
                Agentic Analysis (OpenAI)
              </Typography>
            </Stack>

            <Stack spacing={1.5}>
              <Box>
                <Typography variant="caption" color="text.secondary">Severity</Typography>
                <Chip
                  label={agentic_severity}
                  size="small"
                  color={severityColor(agentic_severity)}
                  sx={{ ml: 1 }}
                />
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Continuous Priority
                </Typography>
                <Typography variant="body2" fontWeight={700}>
                  {agentic_continuous_priority?.toFixed(2) ?? 'N/A'} / 10
                </Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">Probable Team</Typography>
                <Typography variant="body2" fontWeight={600}>
                  {agentic_probable_team}
                </Typography>
              </Box>
              <Divider />
              <Box>
                <Typography variant="caption" color="text.secondary">Reason</Typography>
                <Typography variant="body2" sx={{ whiteSpace: 'pre-line', fontSize: '0.78rem' }}>
                  {agentic_reason}
                </Typography>
              </Box>
            </Stack>
          </Box>
        </Grid>

        {/* Overall + Match Status */}
        <Grid item xs={12} md={4}>
          <Box className="analysis-section analysis-section-overall">
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.5 }}>
              <CheckCircle size={20} />
              <Typography variant="subtitle1" fontWeight={700}>
                Overall Assessment
              </Typography>
            </Stack>

            <Stack spacing={1.5}>
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Average Priority Score
                </Typography>
                <Typography variant="h5" fontWeight={700} color="primary.main">
                  {overall_priority?.toFixed(2)} / 10
                </Typography>
              </Box>

              <Box>
                <Typography variant="caption" color="text.secondary">
                  Severity Match
                </Typography>
                <Stack direction="row" alignItems="center" spacing={0.5}>
                  {severity_match ? (
                    <ArrowUp size={16} className="match-up" />
                  ) : (
                    <ArrowDown size={16} className="match-down" />
                  )}
                  <Typography variant="body2" fontWeight={600}>
                    {severity_match ? 'Agreed' : 'Disagreed'}
                  </Typography>
                  <Typography variant="caption">
                    ({semantic_severity} vs {agentic_severity})
                  </Typography>
                </Stack>
              </Box>

              <Box>
                <Typography variant="caption" color="text.secondary">
                  Team Match
                </Typography>
                <Stack direction="row" alignItems="center" spacing={0.5}>
                  {team_match ? (
                    <ArrowUp size={16} className="match-up" />
                  ) : (
                    <ArrowDown size={16} className="match-down" />
                  )}
                  <Typography variant="body2" fontWeight={600}>
                    {team_match ? 'Agreed' : 'Disagreed'}
                  </Typography>
                </Stack>
                <Typography variant="caption" color="text.secondary">
                  Semantic: {semantic_probable_team}
                  <br />
                  Agentic: {agentic_probable_team}
                </Typography>
              </Box>

              <Divider />

              <Box>
                <Typography variant="caption" color="text.secondary">
                  Recommendation
                </Typography>
                <Typography variant="body2" sx={{ fontSize: '0.78rem' }}>
                  {overall_recommendation}
                </Typography>
              </Box>
            </Stack>
          </Box>
        </Grid>
      </Grid>

      {/* Similar Incidents */}
      {similar_incidents.length > 0 && (
        <Box sx={{ mt: 3 }}>
          <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>
            <FileText size={16} style={{ verticalAlign: 'middle', marginRight: 6 }} />
            Top Similar Historical Incidents
          </Typography>
          {similar_incidents.map((sim, i) => (
            <Box key={i} className="similar-incident-item">
              <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Stack direction="row" spacing={1} alignItems="center">
                  <Typography variant="body2" fontWeight={700}>
                    #{sim.IncidentID}
                  </Typography>
                  <Chip
                    label={`${(sim.similarity_score * 100).toFixed(1)}% match`}
                    size="small"
                    color="info"
                    variant="outlined"
                  />
                  <Chip
                    label={sim.OwnedByTeam}
                    size="small"
                    variant="outlined"
                  />
                  <Chip
                    label={`P${sim.Priority}`}
                    size="small"
                  />
                </Stack>
                <Typography variant="caption" color="text.secondary">
                  {sim.Category} • {sim.Service || sim.Status}
                </Typography>
              </Stack>
            </Box>
          ))}
        </Box>
      )}
    </>
  );

  // ── Inline mode — render directly on the page (no wrapper) ──
  if (inline) {
    return <>{content}</>;
  }

  // ── Dialog mode — original popup behavior ──
  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: { borderRadius: 3, maxHeight: '90vh' },
      }}
    >
      <DialogTitle sx={{ pb: 0 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Typography variant="h6" fontWeight={700} color="primary.main">
            Analysis: Incident #{incident_id}
          </Typography>
          <IconButton onClick={onClose} size="small">
            <X size={20} />
          </IconButton>
        </Stack>
      </DialogTitle>
      <DialogContent dividers sx={{ pt: 2 }}>
        {content}
      </DialogContent>
    </Dialog>
  );
};

export default AnalysisPanel;
